import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  autoCarouselIntervals = [];
  autoCarouselResumes = [];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupAutoCarouselListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#stopAutoCarousels();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          trapFocus(details);
          if (details === this.refs.details) this.#startAutoCarousels();
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    if (details === this.refs.details) this.#stopAutoCarousels();

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  #setupAutoCarouselListeners() {
    const lists = this.querySelectorAll('.menu-drawer__featured-content-list');
    lists.forEach((list) => {
      const pause = () => this.#pauseAutoCarousel(list);
      const resume = () => this.#resumeAutoCarousel(list);

      list.addEventListener('pointerenter', pause);
      list.addEventListener('pointerleave', resume);
      list.addEventListener('touchstart', pause, { passive: true });
      list.addEventListener('scroll', () => {
        if (!list.dataset.autoCarouselActive) return;
        clearTimeout(list.__autoCarouselScrollTimeout);
        list.__autoCarouselScrollTimeout = setTimeout(() => resume(), 1200);
      });
    });
  }

  #startAutoCarousels() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const lists = this.querySelectorAll('.menu-drawer__featured-content-list');
    lists.forEach((list) => {
      if (!(list instanceof HTMLElement)) return;
      if (list.children.length < 2) return;
      if (list.scrollWidth <= list.clientWidth + 8) return;
      if (list.dataset.autoCarouselActive === 'true') return;

      list.dataset.autoCarouselActive = 'true';

      const step = () => {
        const firstCard = list.querySelector('.menu-drawer__featured-content-list-item');
        if (!(firstCard instanceof HTMLElement)) return;

        const cardWidth = firstCard.getBoundingClientRect().width;
        const styles = window.getComputedStyle(list);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
        const delta = Math.max(cardWidth + gap, 1);
        const maxScrollLeft = list.scrollWidth - list.clientWidth;
        const nextScrollLeft = list.scrollLeft + delta;

        list.scrollTo({
          left: nextScrollLeft >= maxScrollLeft - 4 ? 0 : nextScrollLeft,
          behavior: 'smooth',
        });
      };

      const intervalId = window.setInterval(step, 2800);
      this.autoCarouselIntervals.push({ list, intervalId });
    });
  }

  #stopAutoCarousels() {
    this.autoCarouselIntervals.forEach(({ intervalId, list }) => {
      window.clearInterval(intervalId);
      if (list instanceof HTMLElement) delete list.dataset.autoCarouselActive;
    });
    this.autoCarouselIntervals = [];

    this.autoCarouselResumes.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.autoCarouselResumes = [];
  }

  #pauseAutoCarousel(list) {
    this.autoCarouselIntervals = this.autoCarouselIntervals.filter((entry) => {
      if (entry.list !== list) return true;
      window.clearInterval(entry.intervalId);
      return false;
    });
    delete list.dataset.autoCarouselActive;
  }

  #resumeAutoCarousel(list) {
    if (!(list instanceof HTMLElement)) return;
    if (!this.isOpen) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    if (list.dataset.autoCarouselActive === 'true') return;
    if (list.scrollWidth <= list.clientWidth + 8) return;

    const resumeId = window.setTimeout(() => {
      if (!this.isOpen) return;
      if (list.dataset.autoCarouselActive === 'true') return;

      list.dataset.autoCarouselActive = 'true';
      const intervalId = window.setInterval(() => {
        const firstCard = list.querySelector('.menu-drawer__featured-content-list-item');
        if (!(firstCard instanceof HTMLElement)) return;

        const cardWidth = firstCard.getBoundingClientRect().width;
        const styles = window.getComputedStyle(list);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
        const delta = Math.max(cardWidth + gap, 1);
        const maxScrollLeft = list.scrollWidth - list.clientWidth;
        const nextScrollLeft = list.scrollLeft + delta;

        list.scrollTo({
          left: nextScrollLeft >= maxScrollLeft - 4 ? 0 : nextScrollLeft,
          behavior: 'smooth',
        });
      }, 2800);

      this.autoCarouselIntervals.push({ list, intervalId });
    }, 900);

    this.autoCarouselResumes.push(resumeId);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
